// imports (ajustados)
import React, { useState } from 'react';
import { Clock, MapPin, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { Availability, WeekDay, Location } from '../types';
import { formatDisplayName } from '../utils/nameUtils';

// Update the props interface
interface AvailabilityCardProps {
  availability: Availability;
  currentUserId?: string | null;
  onEdit?: (availability: Availability) => void;
  onDelete?: (availabilityId: string) => void;
  locations: Location[];
}

// Update the component parameters to remove onToggleVisibility
export default function AvailabilityCard({ 
  availability, 
  currentUserId,
  onEdit,
  onDelete,
  locations = []
}: AvailabilityCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOwner = currentUserId === availability.player.id;
  const isLoggedIn = !!currentUserId;

  // Define a cor do card com base no primeiro esporte
  const getCardBgColor = () => {
    const sport = availability.sports[0];
    if (sport === 'padel') return 'bg-blue-100';
    if (sport === 'tennis') return 'bg-yellow-100';
    if (sport === 'beach-tennis') return 'bg-green-100';
    return 'bg-gray-100';
  };
  const getLocationNames = () => {
    return availability.locations
      .map(locationId => {
        const location = locations.find(loc => loc.id === locationId);
        return location ? location.name : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  const getDayName = (day: WeekDay) => {
    const days: Record<WeekDay, string> = {
      monday: 'Seg',
      tuesday: 'Ter',
      wednesday: 'Qua',
      thursday: 'Qui',
      friday: 'Sex',
      saturday: 'Sáb',
      sunday: 'Dom'
    };
    return days[day];
  };

  // Função para ordenar os timeSlots pela ordem correta dos dias da semana
  const getSortedTimeSlots = () => {
    const dayOrder: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    return [...availability.timeSlots].sort((a, b) => {
      const dayIndexA = dayOrder.indexOf(a.day);
      const dayIndexB = dayOrder.indexOf(b.day);
      
      // Se os dias são diferentes, ordena pela ordem dos dias
      if (dayIndexA !== dayIndexB) {
        return dayIndexA - dayIndexB;
      }
      
      // Se são o mesmo dia, ordena pelo horário de início
      return a.startTime.localeCompare(b.startTime);
    });
  };

  const formatPhoneToWhatsApp = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent('Olá! Vi sua disponibilidade no QueroJogar e estou entrando em contato.');
    return `https://wa.me/55${cleanPhone}?text=${message}`;
  };

  const getGenderLabel = (gender: string | undefined) => {
    if (!gender) return 'Masculino';
    return gender === 'male' ? 'Masculino' : 'Feminino';
  };

  const getPlayingSideLabel = (side: string) => {
    const sides = {
      left: 'Esquerdo',
      right: 'Direito',
      both: 'Ambos'
    };
    return sides[side as keyof typeof sides] || 'Não especificado';
  };

  const formatPhoneNumber = (phone: string | null) => {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
  };

  // Esportes separados por vírgula
  const getSportsLabel = () => {
    const labels = availability.sports.map((sport) => {
      if (sport === 'padel') return 'Padel';
      if (sport === 'beach-tennis') return 'Beach Tennis';
      if (sport === 'tennis') return 'Tênis';
      return sport;
    });
    return labels.join(', ');
  };

  const getPrimaryCategory = () => {
    if (availability.sports.includes('padel')) {
      return availability.player.padel_category || 'Não informada';
    }
    if (availability.sports.includes('beach-tennis')) {
      return availability.player.beach_tennis_category || 'Não informada';
    }
    if (availability.sports.includes('tennis')) {
      return availability.player.tennis_category || 'Não informada';
    }
    return 'Não informada';
  };

  // Helper function to get sport icon letter
  const getSportIcon = () => {
    const primarySport = availability.sports[0];
    switch (primarySport) {
      case 'padel':
        return <span className="text-2xl font-bold text-blue-600">P</span>;
      case 'beach-tennis':
        return <span className="text-2xl font-bold text-green-600">B</span>;
      case 'tennis':
        return <span className="text-2xl font-bold text-yellow-600">T</span>;
      default:
        return <span className="text-2xl font-bold text-blue-600">P</span>;
    }
  };

  // Helper function to get visibility info
  const getVisibilityInfo = () => {
    if (availability.is_public) {
      return { type: 'public', label: 'Público', avatar: null };
    } 
    
    // Check if it has groups
    if (availability.availability_groups && availability.availability_groups.length > 0) {
      const firstGroup = availability.availability_groups[0];
      let groupName = null;
      let groupAvatar = null;
      
      // Handle different possible structures
      if (firstGroup.groups) {
        groupName = firstGroup.groups.name;
        groupAvatar = firstGroup.groups.avatar;
      }
      
      return { 
        type: 'group', 
        label: groupName || 'Grupo',
        avatar: groupAvatar
      };
    }
    
    // Fallback for private without groups
    return { type: 'private', label: 'Privado', avatar: null };
  };

  // Helper function to get small sport icon letter
  const getSmallSportIcon = () => {
    const primarySport = availability.sports[0];
    switch (primarySport) {
      case 'padel':
        return <span className="text-sm font-bold text-blue-600 bg-blue-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">P</span>;
      case 'beach-tennis':
        return <span className="text-sm font-bold text-green-600 bg-green-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">B</span>;
      case 'tennis':
        return <span className="text-sm font-bold text-yellow-600 bg-yellow-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">T</span>;
      default:
        return <span className="text-sm font-bold text-blue-600 bg-blue-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">P</span>;
    }
  };

  // Helper function to get sport avatar background
  const getSportAvatarBg = () => {
    const primarySport = availability.sports[0];
    switch (primarySport) {
      case 'padel':
        return 'bg-blue-200';
      case 'beach-tennis':
        return 'bg-green-200';
      case 'tennis':
        return 'bg-yellow-200';
      default:
        return 'bg-blue-200';
    }
  };

  // Handler de clique do WhatsApp (antes do return)
  const handleWhatsAppClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!isLoggedIn) {
      e.preventDefault();
      alert('Você precisa se logar para conseguir mandar mensagem.');
    }
  };

  // Get visibility info once to avoid multiple calls
  const visibilityInfo = getVisibilityInfo();

  // Função para obter os time slots que devem ser exibidos
  const getDisplayedTimeSlots = () => {
    const sortedSlots = getSortedTimeSlots();
    const maxSlotsToShow = 3;
    
    if (isExpanded || sortedSlots.length <= maxSlotsToShow) {
      return sortedSlots;
    }
    
    return sortedSlots.slice(0, maxSlotsToShow);
  };

  // Verifica se há mais slots para mostrar
  const hasMoreSlots = () => {
    return getSortedTimeSlots().length > 3;
  };

  // Handler para expandir/contrair o card
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getSportColor = (sport: string) => {
    if (sport === 'padel') return 'border-blue-500';
    if (sport === 'tennis') return 'border-yellow-500';
    if (sport === 'beach-tennis') return 'border-green-500';
    return 'border-gray-500';
  };

  return (
    <div className={`availability-card bg-white rounded-lg shadow-sm p-3 border-2 ${getSportColor(availability.sports[0])} hover:shadow-md transition relative`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {availability.player.avatar ? (
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <img
                src={availability.player.avatar}
                alt={`Avatar de ${availability.player.name}`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className={`w-10 h-10 rounded-full ${getSportAvatarBg()} flex items-center justify-center`}>
              <span className={`text-lg font-bold ${
                availability.sports[0] === 'padel' ? 'text-blue-800' : 
                availability.sports[0] === 'beach-tennis' ? 'text-green-800' : 
                availability.sports[0] === 'tennis' ? 'text-yellow-800' : 'text-blue-800'
              }`}>
                {availability.sports[0] === 'padel' ? 'P' : 
                 availability.sports[0] === 'beach-tennis' ? 'B' : 
                 availability.sports[0] === 'tennis' ? 'T' : 'P'}
              </span>
            </div>
          )}
          <div>
            <h3 className="font-bold text-gray-800 text-lg">
              {formatDisplayName(availability.player.name)}
            </h3>
            <p className="text-xs text-gray-500">
              {getSportsLabel()} • {getPrimaryCategory()} • {getGenderLabel(availability.player.gender)} • {getPlayingSideLabel(availability.player.playing_side)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {getSmallSportIcon()}
          {isOwner && (
            <div className="flex gap-1">
              <button
                onClick={() => onEdit?.(availability)}
                className="text-gray-500 hover:text-blue-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                title="Editar disponibilidade"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onDelete?.(availability.id)}
                className="text-gray-500 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                title="Excluir disponibilidade"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 my-2" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <div className="flex items-start gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-gray-500 mt-0.5" />
          <div>
            <p className="font-medium text-gray-700">Local</p>
            <p className="text-gray-600">{getLocationNames()}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 text-gray-500 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-gray-700">Horários</p>
            <div className="flex justify-between items-end">
              <div className="flex flex-col text-gray-600">
                {getDisplayedTimeSlots().map((slot, index) => (
                  <span key={index}>
                    {getDayName(slot.day)}: {slot.startTime} - {slot.endTime}
                  </span>
                ))}
                {hasMoreSlots() && (
                  <button
                    onClick={toggleExpanded}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors font-medium mt-1 text-xs"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="w-3 h-3" />
                        Mostrar menos
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3 h-3" />
                        Ver todos os horários
                      </>
                    )}
                  </button>
                )}
              </div>
              {availability.player.phone && (
                <a
                  href={isLoggedIn ? formatPhoneToWhatsApp(formatPhoneNumber(availability.player.phone)) : '#'}
                  onClick={handleWhatsAppClick}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Abrir conversa no WhatsApp"
                  aria-disabled={!isLoggedIn}
                  className={`inline-flex items-center gap-2 ${isLoggedIn ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'} font-medium text-sm px-3 py-1.5 rounded-lg shadow-sm transition-all duration-200`}
                >
                  <FaWhatsapp className="w-4 h-4" />
                  <span>Chamar no WhatsApp</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}